// ===========================================
// SERVICIO: Resolver de Destinatarios (ORR-69)
// ===========================================
/**
 * Resuelve specs de destinatarios a una lista de emails.
 *
 * El resolver no depende de notificacionesService (evita circular import).
 * El llamador debe inyectar los helpers de audiencia via el parametro `helpers`.
 *
 * Siempre:
 * - Excluye ctx.actorEmail del resultado (evita auto-notificacion)
 * - Deduplica si el mismo email aparece por multiples specs
 */

import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database } from '@/types/database'
import type { DestinatarioSpec, NotificacionOrigen } from '../types/notificaciones'
import type { Permisos } from '../../types/roles'

export interface ResolverContext {
  /** Siempre excluido del resultado */
  actorEmail: string
  /** Solo requerido si se usa spec 'origin' */
  origen?: NotificacionOrigen
  /** ORR-613: con permiso, el fallback de responsables pasa a quienes lo tienen */
  permiso?: keyof Permisos
}

export interface AudienciaHelpers {
  obtenerDestinatariosOrg: (
    client: SupabaseClient<Database>,
    organizacionId: string,
    excludeEmail?: string,
  ) => Promise<string[]>
  obtenerResponsablesCliente: (
    client: SupabaseClient<Database>,
    clienteId: string,
    excludeEmail?: string,
    permiso?: keyof Permisos,
  ) => Promise<string[]>
}

export const destinatariosResolver = {
  async resolver(
    client: SupabaseClient<Database>,
    specs: DestinatarioSpec | DestinatarioSpec[],
    ctx: ResolverContext,
    helpers: AudienciaHelpers,
  ): Promise<string[]> {
    const normalized = Array.isArray(specs) ? specs : [specs]
    const emails = new Set<string>()

    for (const spec of normalized) {
      const resolved = await resolveOne(client, spec, ctx, helpers)
      resolved.forEach((email) => emails.add(email))
    }

    emails.delete(ctx.actorEmail)
    return [...emails]
  },
}

const resolveOne = async (
  client: SupabaseClient<Database>,
  spec: DestinatarioSpec,
  ctx: ResolverContext,
  helpers: AudienciaHelpers,
): Promise<string[]> => {
  if (spec === 'origin') {
    if (!ctx.origen?.actor_email) {
      throw new Error(
        "destinatariosResolver: spec 'origin' requiere origen.actor_email en ctx",
      )
    }
    return [ctx.origen.actor_email]
  }

  const colonIdx = spec.indexOf(':')
  if (colonIdx === -1) {
    throw new Error(`destinatariosResolver: spec invalida '${spec}'`)
  }

  const kind = spec.substring(0, colonIdx)
  const id = spec.substring(colonIdx + 1)

  switch (kind) {
    case 'user':
      return [id]

    case 'org':
      return helpers.obtenerDestinatariosOrg(client, id, ctx.actorEmail)

    case 'responsables':
      return helpers.obtenerResponsablesCliente(client, id, ctx.actorEmail, ctx.permiso)

    default:
      throw new Error(`destinatariosResolver: kind desconocido '${kind}'`)
  }
}
